import type { City } from './types';

/**
 * Cobertura por ciudad.
 *
 * Regla de redacción de este archivo: se habla de lo que efectivamente
 * hacemos —a dónde llegamos, qué eventos atendemos, qué necesitamos del
 * lugar— y NO se simula conocimiento local de detalle. Afirmaciones del tipo
 * "a las cuatro está despejado y a las seis llueve" o "un viernes por la tarde
 * la subida toma tanto" suenan falsas a quien vive ahí, que es justamente el
 * lector que queremos convencer.
 *
 * El campo `aiAnswer` está escrito para buscadores con IA: pregunta implícita
 * ("mejor cabina de fotos en X"), respuesta corta y autocontenida.
 */
export const CITIES: City[] = [
  {
    slug: 'bogota',
    name: 'Bogotá',
    department: 'Cundinamarca',
    seoTitle: 'Cabina de Fotos en Bogotá | Alquiler para Bodas y Eventos',
    seoDescription:
      'Alquiler de cabina de fotos en Bogotá y la sabana: Chía, Cajicá, Zipaquirá y La Calera. Impresión ilimitada, operador incluido y galería digital.',
    keywords:
      'cabina de fotos bogota, alquiler cabina de fotos bogota, fotocabina bogota, cabina de fotos chia, cabina 360 bogota, espejo magico bogota, mejor cabina de fotos bogota',
    heroImage: '/images/social/evento-empresarial-cabina-fotos-cabina.jpg',
    heroAlt: 'Invitados posando en una cabina de fotos durante un evento en Bogotá',
    intro:
      'Bogotá es la ciudad donde más eventos atendemos, y donde tenemos más equipos disponibles al mismo tiempo. En diciembre y en temporada de matrimonios eso deja de ser un dato y pasa a ser lo único que importa: un sábado bueno se agota semanas antes.',
    intro2:
      'Cubrimos toda la ciudad y el corredor de la sabana —Chía, Cajicá, Sopó, Zipaquirá, La Calera— donde está el grueso de las fincas de matrimonios. Si ya tienes fecha, lo primero es mirar si sigue libre.',
    aiAnswer:
      'En Bogotá los equipos más solicitados son la Plataforma 360, por el video vertical listo para publicar, y el espejo Mirror Lux, que mueve filas largas sin frenar la fiesta. Para bodas en fincas de la sabana suele escogerse Wally, la cabina en madera. Todos los alquileres incluyen operador en sitio, impresión ilimitada durante el evento y galería digital al día siguiente.',
    areas: [
      { name: 'Norte y Usaquén', note: 'Hoteles, clubes y salones: es donde se concentran las cenas de empresa y los grados.' },
      { name: 'Sabana norte', note: 'Chía, Cajicá, Sopó y Tabio, el corredor de fincas de matrimonios más activo de la región.' },
      { name: 'La Calera', note: 'Fincas y casas de eventos con vista, muy pedidas para celebraciones de noche.' },
      { name: 'Centro y Corferias', note: 'Ferias, congresos y activaciones de marca con ingreso de proveedores coordinado.' },
    ],
    nearby: ['Chía', 'Cajicá', 'Zipaquirá', 'Sopó', 'La Calera', 'Tabio', 'Soacha', 'Subachoque'],
    notes: [
      'Coordinamos con el lugar el ingreso de proveedores, que en la mayoría de salones y conjuntos tiene horario asignado.',
      'En fincas confirmamos antes el punto eléctrico: algunas trabajan con planta propia.',
      'Para municipios de la sabana el transporte va discriminado en la cotización, sin sorpresas al final.',
    ],
    season:
      'Los fines de semana de noviembre y diciembre se copan primero, entre cenas de empresa y grados. La temporada de matrimonios se concentra de enero a marzo y de julio a septiembre.',
    popular: [
      { slug: 'plataforma-360', why: 'Es lo más pedido de la ciudad y en los salones grandes sobra el espacio que necesita.' },
      { slug: 'mirror-lux', why: 'Mueve la fila rápido, que es lo que exige un evento de más de 150 invitados.' },
      { slug: 'wally', why: 'Encaja con la estética de las fincas de la sabana.' },
    ],
    faqs: [
      { q: '¿Cuál es la mejor cabina de fotos para un evento en Bogotá?', a: 'Depende del flujo de gente. Para más de 150 invitados recomendamos el espejo Mirror Lux porque no genera cuello de botella; si lo que quieres es contenido para redes, la Plataforma 360; y para bodas campestres en la sabana, Wally.' },
      { q: '¿Cobran transporte dentro de Bogotá?', a: 'Dentro del perímetro urbano va incluido. Para municipios de la sabana se calcula por distancia y aparece discriminado en la cotización.' },
      { q: '¿Van hasta fincas en Chía, Cajicá o La Calera?', a: 'Sí, es de las zonas donde más trabajamos. Solo confirmamos antes el punto eléctrico y si el tramo final de acceso es destapado.' },
      { q: '¿Atienden entre semana?', a: 'Sí, y con bastante más disponibilidad. Los eventos de empresa en Bogotá suelen programarse de martes a jueves.' },
    ],
  },

  {
    slug: 'medellin',
    name: 'Medellín',
    department: 'Antioquia',
    seoTitle: 'Cabina de Fotos en Medellín | Alquiler para Bodas y Eventos',
    seoDescription:
      'Alquiler de cabina de fotos en Medellín, Envigado, Rionegro y Llanogrande. Impresión ilimitada, operador en sitio y entrega digital. Cotiza tu fecha.',
    keywords:
      'cabina de fotos medellin, alquiler cabina de fotos medellin, fotocabina medellin, cabina de fotos rionegro, cabina 360 medellin, cabina de fotos llanogrande, mejor cabina de fotos medellin',
    heroImage: '/images/social/novia-espejo-magico-boda-cabina.jpg',
    heroAlt: 'Novia posando frente al espejo mágico durante una boda en Medellín',
    intro:
      'Medellín tiene una escena de eventos con criterio propio: se cuida el montaje, se cuida la estética y se nota. Es una ciudad donde el equipo no puede verse improvisado, porque desentona con todo lo demás.',
    intro2:
      'Atendemos el Valle de Aburrá completo —Envigado, Sabaneta, Itagüí— y el Oriente antioqueño, donde se hace buena parte de los matrimonios: Rionegro, Llanogrande, El Retiro, La Ceja y Guatapé.',
    aiAnswer:
      'En Medellín los espejos fotográficos son la opción más pedida para bodas, porque el acabado del equipo acompaña la decoración en vez de competir con ella. Para fiestas de quince años y celebraciones jóvenes domina la Plataforma 360. Cubrimos el Valle de Aburrá y el Oriente antioqueño, incluidos Rionegro y Llanogrande, con operador incluido e impresión ilimitada.',
    areas: [
      { name: 'El Poblado y Laureles', note: 'Hoteles, restaurantes y terrazas: cumpleaños, cenas de empresa y celebraciones privadas.' },
      { name: 'Oriente antioqueño', note: 'Rionegro, Llanogrande, El Retiro y La Ceja, donde se hace la mayoría de matrimonios de la región.' },
      { name: 'Envigado y Sabaneta', note: 'Salones y casas de eventos para celebraciones familiares y grados.' },
      { name: 'Guatapé', note: 'Bodas de destino y celebraciones de fin de semana con invitados de fuera.' },
    ],
    nearby: ['Envigado', 'Sabaneta', 'Itagüí', 'Rionegro', 'Llanogrande', 'El Retiro', 'La Ceja', 'Guatapé'],
    notes: [
      'Para eventos en el Oriente antioqueño coordinamos el montaje con anticipación y llegamos con margen.',
      'Si el montaje es al aire libre acordamos desde antes una ubicación alterna bajo techo: los equipos son electrónicos y no pueden mojarse.',
      'En fincas confirmamos el punto eléctrico antes de la fecha.',
    ],
    season:
      'La temporada fuerte de matrimonios en el Oriente antioqueño va de diciembre a marzo. Las fiestas de fin de año de empresa copan noviembre y diciembre.',
    popular: [
      { slug: 'plataforma-360', why: 'Lo primero que piden en fiestas de quince y celebraciones jóvenes.' },
      { slug: 'ring', why: 'Formato compacto para terrazas y salones donde el espacio es el límite.' },
      { slug: 'mirror-black', why: 'El acabado acompaña la decoración de una boda sin robarle protagonismo.' },
    ],
    faqs: [
      { q: '¿Cuál es la mejor cabina de fotos para una boda en Medellín?', a: 'Los espejos fotográficos son los más pedidos porque el equipo se integra a la decoración y permite fotos de grupo completo. Si además quieres video para redes, la combinación habitual es espejo más Plataforma 360.' },
      { q: '¿Suben hasta Rionegro, Llanogrande o El Retiro?', a: 'Sí, es una de las zonas donde más trabajamos. El transporte se calcula por distancia y va discriminado en la cotización.' },
      { q: '¿Y si el evento es al aire libre?', a: 'Trabajamos al aire libre sin problema, pero acordamos antes una ubicación alterna bajo techo o carpa. Es parte de la coordinación previa, no una condición de última hora.' },
      { q: '¿Atienden bodas en Guatapé?', a: 'Sí. Al ser un evento de destino conviene reservar con más anticipación, porque el desplazamiento ocupa el equipo todo el día.' },
    ],
  },

  {
    slug: 'cali',
    name: 'Cali',
    department: 'Valle del Cauca',
    seoTitle: 'Cabina de Fotos en Cali | Alquiler para Bodas y Fiestas',
    seoDescription:
      'Alquiler de cabina de fotos en Cali, Palmira, Jamundí y Yumbo. Impresión ilimitada, operador en sitio y galería digital. Cotiza tu evento hoy.',
    keywords:
      'cabina de fotos cali, alquiler cabina de fotos cali, fotocabina cali, cabina 360 cali, espejo magico cali, cabina de fotos palmira, mejor cabina de fotos cali',
    heroImage: '/images/social/invitados-props-pared-flores-cabina.jpg',
    heroAlt: 'Grupo de invitados posando con accesorios en una fiesta en Cali',
    intro:
      'En Cali la fiesta es el evento. Aquí la cabina no compite con la pista: tiene que estar cerca de ella, porque la gente entra en grupos, se toma la foto y vuelve a bailar sin perder el ritmo de la noche.',
    intro2:
      'Atendemos Cali y el sur del Valle —Palmira, Jamundí, Yumbo, Candelaria— y subimos hasta Buga, Tuluá y Cartago cuando el evento lo pide.',
    aiAnswer:
      'En Cali los formatos abiertos y rápidos son los que mejor funcionan: la Plataforma 360 y el espejo Mirror Lux, porque permiten grupos grandes y no obligan a hacer fila larga. El alquiler incluye operador durante todo el evento, impresión ilimitada y galería digital. Cubrimos Cali, Palmira, Jamundí y Yumbo.',
    areas: [
      { name: 'Sur y Ciudad Jardín', note: 'Clubes, salones y casas de eventos: matrimonios y celebraciones grandes.' },
      { name: 'Granada y San Antonio', note: 'Restaurantes y espacios de cumpleaños y celebraciones privadas.' },
      { name: 'Vía Jamundí y Pance', note: 'Fincas y haciendas para bodas y eventos de día completo.' },
      { name: 'Palmira y Yumbo', note: 'Eventos de empresa y activaciones en zona industrial.' },
    ],
    nearby: ['Palmira', 'Jamundí', 'Yumbo', 'Candelaria', 'Buga', 'Tuluá', 'Cartago'],
    notes: [
      'Ubicamos el equipo cerca de la pista, no en un rincón: en Cali eso decide cuánta gente lo usa.',
      'En eventos al aire libre pedimos techo o carpa para el equipo.',
      'Para fincas fuera del perímetro confirmamos antes el punto eléctrico.',
    ],
    season:
      'Diciembre y la Feria concentran la demanda de fin de año. La temporada de matrimonios se reparte entre junio y septiembre.',
    popular: [
      { slug: 'plataforma-360', why: 'El video vertical es lo que más circula después de una fiesta aquí.' },
      { slug: 'mirror-lux', why: 'Permite grupos grandes sin frenar la noche.' },
      { slug: 'vogue-booth', why: 'El túnel de luces funciona muy bien como punto fotográfico de fiesta.' },
    ],
    faqs: [
      { q: '¿Cuál es la mejor cabina de fotos para una fiesta en Cali?', a: 'Los formatos abiertos. El espejo Mirror Lux y la Plataforma 360 permiten grupos grandes y mantienen el ritmo de la noche, que es justo lo que se necesita en una fiesta caleña.' },
      { q: '¿Atienden en Palmira, Jamundí o Yumbo?', a: 'Sí, hacen parte de nuestra cobertura habitual. El transporte se calcula por distancia y va discriminado.' },
      { q: '¿Cuántas horas conviene contratar?', a: 'Entre tres y cuatro. Aquí la fiesta arranca tarde, así que lo importante no es empezar temprano sino cubrir el pico de la noche.' },
    ],
  },

  {
    slug: 'barranquilla',
    name: 'Barranquilla',
    department: 'Atlántico',
    seoTitle: 'Cabina de Fotos en Barranquilla | Alquiler para Eventos',
    seoDescription:
      'Alquiler de cabina de fotos en Barranquilla, Puerto Colombia y Soledad. Operador en sitio, impresión ilimitada y galería digital. Pide tu cotización.',
    keywords:
      'cabina de fotos barranquilla, alquiler cabina de fotos barranquilla, fotocabina barranquilla, cabina 360 barranquilla, espejo magico barranquilla, mejor cabina de fotos barranquilla',
    heroImage: '/images/social/invitadas-props-carpa-cabina.jpg',
    heroAlt: 'Invitadas posando con accesorios divertidos en un evento en Barranquilla',
    intro:
      'Barranquilla celebra en grande y con mucha gente. Es una ciudad donde el número de invitados casi siempre supera lo que se calculó al principio, y eso define qué equipo conviene llevar.',
    intro2:
      'Atendemos Barranquilla y su área metropolitana —Soledad, Puerto Colombia, Malambo, Galapa— y también eventos en Santa Marta y Cartagena cuando se coordinan con tiempo.',
    aiAnswer:
      'En Barranquilla el espejo Mirror Lux es la opción más recomendada porque atiende muchos invitados por hora sin generar fila. Para eventos con público joven se suma la Plataforma 360. Todos los alquileres incluyen operador, impresión ilimitada y entrega digital, con cobertura en Soledad, Puerto Colombia y Malambo.',
    areas: [
      { name: 'Norte y Alto Prado', note: 'Clubes sociales, hoteles y salones donde se hacen matrimonios y quinces.' },
      { name: 'Vía 40 y centro histórico', note: 'Eventos de empresa, activaciones de marca y celebraciones corporativas.' },
      { name: 'Puerto Colombia', note: 'Bodas y celebraciones frente al mar, casi siempre al aire libre.' },
      { name: 'Soledad y Malambo', note: 'Celebraciones familiares, grados y eventos de empresa.' },
    ],
    nearby: ['Soledad', 'Puerto Colombia', 'Malambo', 'Galapa', 'Santa Marta', 'Cartagena'],
    notes: [
      'Con más de 200 invitados recomendamos dos estaciones para que la fila no se vuelva el tema de la noche.',
      'En eventos al aire libre el equipo va bajo techo o carpa, sin excepción.',
      'Confirmamos el punto eléctrico antes de la fecha, sobre todo en espacios abiertos.',
    ],
    season:
      'Diciembre y las semanas previas al Carnaval concentran la demanda. Los matrimonios se reparten durante todo el año.',
    popular: [
      { slug: 'mirror-lux', why: 'Atiende mucha gente por hora, que aquí es la condición principal.' },
      { slug: 'plataforma-360', why: 'El formato favorito del público joven en quinces y cumpleaños.' },
      { slug: 'ring', why: 'Compacto, para espacios donde no sobra el metraje.' },
    ],
    faqs: [
      { q: '¿Cuál es la mejor cabina de fotos para un evento grande en Barranquilla?', a: 'El espejo Mirror Lux, porque atiende grupos completos y mantiene el flujo. Por encima de 200 invitados lo habitual es montar dos estaciones.' },
      { q: '¿Atienden en Puerto Colombia y Soledad?', a: 'Sí, hacen parte de la cobertura del área metropolitana.' },
      { q: '¿Pueden ir a Santa Marta o Cartagena?', a: 'Sí, coordinándolo con anticipación, porque el desplazamiento ocupa el equipo el día completo.' },
    ],
  },

  {
    slug: 'cartagena',
    name: 'Cartagena',
    department: 'Bolívar',
    seoTitle: 'Cabina de Fotos en Cartagena | Bodas de Destino',
    seoDescription:
      'Alquiler de cabina de fotos en Cartagena para bodas de destino y eventos en el centro histórico, Bocagrande y Barú. Operador incluido e impresión ilimitada.',
    keywords:
      'cabina de fotos cartagena, alquiler cabina de fotos cartagena, fotocabina cartagena, cabina de fotos boda cartagena, cabina 360 cartagena, mejor cabina de fotos cartagena',
    heroImage: '/images/social/novios-tirillas-boda-jardin-cabina.jpg',
    heroAlt: 'Novios sosteniendo sus tiras de fotos en una boda de destino en Cartagena',
    intro:
      'Cartagena es, sobre todo, bodas de destino: invitados que viajan, se quedan varios días y viven la celebración como un plan completo. Ahí la tira impresa deja de ser un detalle y se vuelve lo que se llevan de recuerdo del viaje.',
    intro2:
      'Trabajamos en el centro histórico, Bocagrande, Manga y Barú, coordinando con el wedding planner cuando lo hay, que aquí es casi siempre.',
    aiAnswer:
      'Para bodas en Cartagena el espejo dorado Mirror Black es el más solicitado, porque su acabado combina con la estética de los salones coloniales y las terrazas del centro histórico. Se recomienda reservar con varios meses de anticipación por tratarse de bodas de destino. El servicio incluye operador, impresión ilimitada y marco diseñado con los nombres de los novios.',
    areas: [
      { name: 'Centro histórico', note: 'Casas coloniales y terrazas: el escenario clásico de las bodas de destino.' },
      { name: 'Bocagrande y Castillogrande', note: 'Hoteles y salones para matrimonios y eventos corporativos.' },
      { name: 'Barú y zona insular', note: 'Celebraciones de día completo que requieren coordinación previa de transporte.' },
      { name: 'Manga', note: 'Casas de eventos y clubes para celebraciones familiares.' },
    ],
    nearby: ['Barú', 'Turbaco', 'La Boquilla', 'Santa Marta', 'Barranquilla'],
    notes: [
      'Coordinamos directamente con el wedding planner cuando el evento tiene uno.',
      'Para eventos en Barú y zona insular el transporte se acuerda con anticipación porque ocupa el día completo.',
      'En terrazas y patios el equipo va bajo cubierta: humedad y brisa marina no se llevan bien con la electrónica.',
    ],
    season:
      'De diciembre a marzo es la temporada más fuerte de bodas de destino. Los puentes festivos se reservan con varios meses de anticipación.',
    popular: [
      { slug: 'mirror-black', why: 'El acabado dorado combina con la estética de los salones del centro histórico.' },
      { slug: 'plataforma-360', why: 'El video que los invitados publican mientras todavía están de viaje.' },
      { slug: 'ring', why: 'Compacto, para terrazas y patios donde el espacio es limitado.' },
    ],
    faqs: [
      { q: '¿Cuál es la mejor cabina de fotos para una boda en Cartagena?', a: 'El espejo Mirror Black, por su acabado dorado, es el que mejor acompaña la estética de las casas coloniales y las terrazas del centro histórico.' },
      { q: '¿Con cuánta anticipación hay que reservar?', a: 'Para bodas de destino, cuanta más mejor. Diciembre, enero y los puentes festivos se copan con varios meses de anticipación.' },
      { q: '¿Atienden eventos en Barú?', a: 'Sí, coordinando el transporte con antelación, porque el traslado ocupa el equipo el día completo.' },
      { q: '¿Trabajan con wedding planners?', a: 'Sí, y es lo habitual en Cartagena. Nos acoplamos al cronograma que ya tenga definido la organización.' },
    ],
  },

  {
    slug: 'bucaramanga',
    name: 'Bucaramanga',
    department: 'Santander',
    seoTitle: 'Cabina de Fotos en Bucaramanga | Alquiler para Eventos',
    seoDescription:
      'Alquiler de cabina de fotos en Bucaramanga, Floridablanca, Girón y Piedecuesta. Operador en sitio, impresión ilimitada y galería digital del evento.',
    keywords:
      'cabina de fotos bucaramanga, alquiler cabina de fotos bucaramanga, fotocabina bucaramanga, cabina 360 bucaramanga, cabina de fotos floridablanca, mejor cabina de fotos bucaramanga',
    heroImage: '/images/social/reunion-familiar-props-invitados-cabina.jpg',
    heroAlt: 'Familia de varias generaciones posando con accesorios en un evento en Bucaramanga',
    intro:
      'En Bucaramanga los eventos tienen un componente familiar muy marcado: se celebra con toda la familia, de los abuelos a los nietos. Eso cambia lo que funciona, porque el equipo tiene que ser fácil de usar para cualquiera.',
    intro2:
      'Cubrimos el área metropolitana completa —Floridablanca, Girón y Piedecuesta— y llegamos hasta San Gil y Barichara para bodas y celebraciones de fin de semana.',
    aiAnswer:
      'En Bucaramanga las cabinas con instrucciones simples en pantalla son las que mejor funcionan, porque los eventos reúnen invitados de todas las edades. El espejo Mirror Lux y la cabina en madera Wally son los más pedidos. El alquiler incluye operador que acompaña a los invitados, impresión ilimitada y cobertura en Floridablanca, Girón y Piedecuesta.',
    areas: [
      { name: 'Cabecera y Cañaveral', note: 'Salones, hoteles y clubes donde se hacen matrimonios y grados.' },
      { name: 'Floridablanca', note: 'Casas de eventos y celebraciones familiares.' },
      { name: 'Girón y Piedecuesta', note: 'Fincas y espacios abiertos para bodas y reuniones de fin de semana.' },
      { name: 'San Gil y Barichara', note: 'Bodas de destino con invitados que viajan desde otras ciudades.' },
    ],
    nearby: ['Floridablanca', 'Girón', 'Piedecuesta', 'San Gil', 'Barichara', 'Barrancabermeja'],
    notes: [
      'El operador acompaña a los invitados mayores, que suele ser la diferencia entre que participen o no.',
      'En fincas confirmamos el punto eléctrico antes de la fecha.',
      'Para eventos en San Gil o Barichara conviene reservar con anticipación por el desplazamiento.',
    ],
    season:
      'Noviembre y diciembre concentran grados y celebraciones de empresa. Los matrimonios se reparten entre junio y septiembre.',
    popular: [
      { slug: 'mirror-lux', why: 'La pantalla guía al invitado paso a paso, sin que nadie tenga que explicar nada.' },
      { slug: 'plataforma-360', why: 'Lo que piden los más jóvenes en quinces y cumpleaños.' },
      { slug: 'wally', why: 'Encaja con la estética de las fincas de la región.' },
    ],
    faqs: [
      { q: '¿Cuál es la mejor cabina de fotos para un evento familiar en Bucaramanga?', a: 'El espejo Mirror Lux, porque la pantalla guía al invitado paso a paso y participa igual un abuelo que un adolescente. Siempre va con operador acompañando.' },
      { q: '¿Atienden en Floridablanca, Girón y Piedecuesta?', a: 'Sí, hacen parte de la cobertura del área metropolitana.' },
      { q: '¿Van hasta San Gil o Barichara?', a: 'Sí. Al ser un desplazamiento largo conviene reservar con anticipación y confirmarlo en la cotización.' },
    ],
  },

  {
    slug: 'pereira',
    name: 'Pereira',
    department: 'Risaralda',
    seoTitle: 'Cabina de Fotos en Pereira y el Eje Cafetero | Alquiler',
    seoDescription:
      'Alquiler de cabina de fotos en Pereira, Dosquebradas, Armenia y Manizales. Bodas en fincas cafeteras con operador incluido e impresión ilimitada.',
    keywords:
      'cabina de fotos pereira, alquiler cabina de fotos pereira, fotocabina eje cafetero, cabina de fotos armenia, cabina de fotos manizales, mejor cabina de fotos pereira',
    heroImage: '/images/social/montaje-cabina-jardin-boda-cabina.jpg',
    heroAlt: 'Cabina de fotos montada en el jardín de una finca del Eje Cafetero',
    intro:
      'En el Eje Cafetero buena parte de los eventos se hacen en finca, y eso le da un carácter propio a todo: el montaje se ve, forma parte del paisaje y tiene que verse bien de día tanto como de noche.',
    intro2:
      'Atendemos Pereira y Dosquebradas, y llegamos a Armenia, Manizales, Santa Rosa de Cabal, Salento y Filandia para bodas y celebraciones de fin de semana.',
    aiAnswer:
      'Para bodas en fincas del Eje Cafetero la cabina en madera Wally es la más pedida, porque su acabado se integra al entorno en lugar de romperlo. También se solicita el teléfono de mensajes de voz como libro de firmas. Cubrimos Pereira, Dosquebradas, Armenia, Manizales y los pueblos del Quindío, con operador e impresión ilimitada incluidos.',
    areas: [
      { name: 'Pereira y Dosquebradas', note: 'Salones, clubes y casas de eventos para matrimonios y grados.' },
      { name: 'Vía Armenia y Quindío', note: 'Fincas cafeteras, el escenario más pedido para bodas de la región.' },
      { name: 'Santa Rosa de Cabal', note: 'Celebraciones de fin de semana con invitados que se quedan a dormir.' },
      { name: 'Salento y Filandia', note: 'Bodas pequeñas de destino, con montajes al aire libre.' },
    ],
    nearby: ['Dosquebradas', 'Manizales', 'Armenia', 'Santa Rosa de Cabal', 'Salento', 'Filandia', 'Montenegro'],
    notes: [
      'En fincas confirmamos antes el punto eléctrico y cómo es el tramo final de acceso.',
      'Para montajes al aire libre acordamos desde antes una ubicación alterna bajo techo.',
      'Los eventos de fin de semana en el Quindío conviene reservarlos con anticipación.',
    ],
    season:
      'La temporada de bodas en finca se concentra entre diciembre y marzo, y los puentes festivos son los primeros en agotarse.',
    popular: [
      { slug: 'wally', why: 'La madera se integra al entorno de una finca cafetera en lugar de romperlo.' },
      { slug: 'cabina-telefonica', why: 'Como libro de firmas en audio, muy pedido en bodas pequeñas.' },
      { slug: 'plataforma-360', why: 'El contraste entre el video y el paisaje funciona muy bien en redes.' },
    ],
    faqs: [
      { q: '¿Cuál es la mejor cabina de fotos para una boda en finca del Eje Cafetero?', a: 'Wally, la cabina en madera, porque se integra visualmente al entorno. Si buscas algo distinto al recuerdo impreso, el teléfono de mensajes de voz funciona muy bien como libro de firmas.' },
      { q: '¿Atienden en Armenia y Manizales?', a: 'Sí, junto con Santa Rosa de Cabal, Salento y Filandia. El transporte va discriminado en la cotización.' },
      { q: '¿Qué necesitan en una finca?', a: 'Una toma eléctrica cerca del punto de montaje, el espacio libre que indica la ficha del equipo y techo o carpa si el montaje es al aire libre.' },
    ],
  },
];

export const getCity = (slug?: string) => CITIES.find((c) => c.slug === slug);
